/**
 * Duration formatting for elapsed time (turnaround, stage timings, cycle length).
 *
 * Rule: a duration is never shown as a fraction of a unit. "12 hours" is written
 * as `12 h`, never `0.5 d`; "90 minutes" is `1 h 30 m`, never `1.5 h`. Decimal
 * time reads as a measurement error to the people using these reports, and the
 * two obvious workarounds are both worse:
 *
 *   - rounding decimal days (Math.round(12h / 24) -> "1 d", and 6h -> "0 d")
 *     makes a real stage look instantaneous;
 *   - printing one decimal ("12.5 h") is exactly what we are told to avoid.
 *
 * So the unit is chosen to fit the magnitude and the number stays whole.
 */

const HOURS_PER_DAY = 24;

/**
 * Format a duration given in hours as whole units.
 *
 *   0            -> "0 m"
 *   0.4  (24m)   -> "24 m"
 *   12           -> "12 h"
 *   12.5         -> "12 h 30 m"
 *   36           -> "1 d 12 h"
 *   72           -> "3 d"
 *
 * @param {number|string|null|undefined} hours
 * @param {{ compact?: boolean }} [opts] compact drops the smaller unit once the
 *   value is at least a day, so a tight column reads "3 d" rather than "3 d 4 h".
 * @returns {string}
 */
export const formatHours = (hours, opts = {}) => {
  const n = Number(hours);
  if (!Number.isFinite(n) || n <= 0) return '0 m';

  const totalMinutes = Math.round(n * 60);
  if (totalMinutes < 60) return `${totalMinutes} m`;

  const totalHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (totalHours < HOURS_PER_DAY) {
    return minutes ? `${totalHours} h ${minutes} m` : `${totalHours} h`;
  }

  const d = Math.floor(totalHours / HOURS_PER_DAY);
  const h = totalHours % HOURS_PER_DAY;
  if (opts.compact || h === 0) return `${d} d`;
  return `${d} d ${h} h`;
};

/**
 * Same rule, for a value already expressed in days (e.g. a summed cycle length).
 * Converts to hours first so a part-day never renders as a fraction.
 */
export const formatDays = (days, opts = {}) => formatHours((Number(days) || 0) * HOURS_PER_DAY, opts);
